import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Phone, MapPin, Globe, ArrowUpRight, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SERVICES = ["Architecture & Planning", "Structural Engineering", "MEP Engineering", "BIM & Technical Services", "Visualization", "Other"];

const CONTACTS = [
  { icon: Mail, label: "Email", value: "contact@starscapestudio.in", href: "mailto:contact@starscapestudio.in" },
  { icon: Phone, label: "Phone", value: "+91 82871 94750", href: "tel:+918287194750" },
  { icon: Globe, label: "Web", value: "www.starscapestudio.in", href: "https://www.starscapestudio.in" },
  { icon: MapPin, label: "Office", value: "New Delhi, India", href: null },
];

const inputCls =
  "w-full bg-transparent border-b border-white/20 focus:border-white px-0 py-3 text-sm font-light placeholder:text-neutral-600 outline-none transition-colors duration-300 rounded-none";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", company: "", service: "", message: "" });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      toast.success("Enquiry sent. We'll get back to you shortly.");
      setForm({ name: "", email: "", company: "", service: "", message: "" });
    } catch {
      toast.error("Something went wrong. Please email us directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="py-28 lg:py-36" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-16 lg:gap-24">
        <motion.div initial={{ opacity: 0, y: 28 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8 }}>
          <p className="font-mono-tech text-xs tracking-[0.35em] uppercase text-neutral-500 mb-4">07 / Contact</p>
          <h2 className="font-heading font-black uppercase tracking-tighter text-4xl sm:text-5xl lg:text-6xl leading-[0.95] mb-8">
            Let's<br />Connect
          </h2>
          <p className="text-sm text-neutral-400 font-light leading-relaxed max-w-sm mb-12">
            Tell us about your project — a villa, a township, a retrofit or a full BIM mandate. We respond within one working day.
          </p>
          <div className="space-y-6">
            {CONTACTS.map((c) => (
              <div key={c.label} className="flex items-center gap-4" data-testid={`contact-detail-${c.label.toLowerCase()}`}>
                <c.icon size={16} className="text-neutral-500 shrink-0" />
                <div>
                  <p className="font-mono-tech text-[10px] tracking-[0.3em] uppercase text-neutral-600">{c.label}</p>
                  {c.href ? (
                    <a href={c.href} className="text-sm text-neutral-200 hover:text-white transition-colors duration-300">
                      {c.value}
                    </a>
                  ) : (
                    <p className="text-sm text-neutral-200">{c.value}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </motion.div>

        <motion.form
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.15 }}
          onSubmit={submit}
          className="space-y-8 self-center"
          data-testid="contact-form"
        >
          <div className="grid sm:grid-cols-2 gap-8">
            <input required value={form.name} onChange={set("name")} placeholder="Your name *" className={inputCls} data-testid="contact-name-input" />
            <input required type="email" value={form.email} onChange={set("email")} placeholder="Email *" className={inputCls} data-testid="contact-email-input" />
          </div>
          <input value={form.company} onChange={set("company")} placeholder="Company / organization" className={inputCls} data-testid="contact-company-input" />
          <select value={form.service} onChange={set("service")} className={`${inputCls} bg-black ${form.service ? "text-white" : "text-neutral-600"}`} data-testid="contact-service-select">
            <option value="" disabled>Service you're interested in</option>
            {SERVICES.map((s) => (
              <option key={s} value={s} className="bg-black text-white">{s}</option>
            ))}
          </select>
          <textarea required rows={4} value={form.message} onChange={set("message")} placeholder="Tell us about your project *" className={`${inputCls} resize-none`} data-testid="contact-message-input" />
          <button
            type="submit"
            disabled={loading}
            data-testid="contact-submit-button"
            className="group flex items-center gap-2 border border-white px-8 py-4 font-mono-tech text-xs tracking-[0.25em] uppercase hover:bg-white hover:text-black transition-colors duration-300 disabled:opacity-50"
          >
            {loading ? <Loader2 size={14} className="animate-spin" /> : <ArrowUpRight size={14} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />}
            {loading ? "Sending" : "Send Enquiry"}
          </button>
        </motion.form>
      </div>
    </section>
  );
}
